import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useLayout } from '../hooks/useLayout';
import LayOutHeader from './layOutHeader';
import LayoutSideNav from './layoutSideNav';
import LayOutBody from './layOutBody';
import TabletSideNavDrawer from './tabletSideNavDrawer';
import { useAuthViewport } from '@/epics/authentication/pages/login/AuthTemplate/useAuthViewport';
import { CompleteProfileModal } from '@/shared/components/CompleteProfileModal';
import { UserPrivilegesProvider } from '@/shared/context/UserPrivilegesContext';

export type LayoutType = {
  children: React.ReactNode;
};

const LayOut: React.FC<LayoutType> = ({ children }) => {
  const {
    isCollapsed,
    toggleCollapse,
    setCollapsed,
    logoSrc,
    sidebarStyle,
    headerStyle,
    contentAreaStyle,
    logoStyle,
  } = useLayout();
  const { isTablet } = useAuthViewport();
  const navigate = useNavigate();

  // Show "Complete Profile" modal on first login.
  // The Layout remounts on every navigation (Routes key changes), so we
  // persist the dismissal in sessionStorage to prevent the modal from
  // reappearing after the user skips or navigates to the edit-profile page.
  const MODAL_DISMISSED_KEY = 'complete-profile-modal-dismissed';
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(MODAL_DISMISSED_KEY)) {
      return;
    }

    const checkFirstLogin = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;

        if (idToken) {
          // Decode JWT payload (second part, base64url encoded)
          const parts = idToken.toString().split('.');
          if (parts.length === 3 && parts[1]) {
            const payload = JSON.parse(
              atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
            ) as Record<string, unknown>;
            const loginCount = parseInt(
              String(payload['custom:login_count'] ?? '0'),
              10
            );

            // Show modal only on second login (login_count === 2).
            // Mark it as seen immediately so a page refresh (which re-runs
            // this effect with the same token) does not reopen it.
            if (loginCount === 2) {
              sessionStorage.setItem(MODAL_DISMISSED_KEY, '1');
              setShowProfileModal(true);
            }
          }
        }
      } catch (error) {
        console.error('Failed to check login count:', error);
      }
    };

    checkFirstLogin();
  }, []);

  const dismissModal = useCallback(() => {
    setShowProfileModal(false);
    sessionStorage.setItem(MODAL_DISMISSED_KEY, '1');
  }, [MODAL_DISMISSED_KEY]);

  const handleSkipProfileModal = useCallback(() => {
    dismissModal();
  }, [dismissModal]);

  const handleConfigureProfile = useCallback(() => {
    dismissModal();
    sessionStorage.setItem('profile-first-login-flow', 'true');
    navigate('/Profil/Edytuj-profil');
  }, [dismissModal, navigate]);

  // On tablet, the sidebar is a fixed overlay — content always fills full width.
  const tabletFullWidthStyle: React.CSSProperties = isTablet
    ? { marginLeft: '88px', width: 'calc(100vw - 88px)' }
    : {};

  const effectiveSidebarWidth = isTablet
    ? '88px'
    : (sidebarStyle.width as string);

  return (
    <UserPrivilegesProvider>
      <div
        className="w-[100vw] h-[100vh] overflow-hidden relative"
        style={
          { '--sidebar-width': effectiveSidebarWidth } as React.CSSProperties
        }
      >
        <LayOutHeader
          headerStyle={{
            ...headerStyle,
            ...(isTablet && !isCollapsed ? tabletFullWidthStyle : {}),
          }}
          toggleCollapse={toggleCollapse}
          isCollapsed={isCollapsed}
        />
        <LayOutBody
          contentAreaStyle={{
            ...contentAreaStyle,
            ...(isTablet && !isCollapsed ? tabletFullWidthStyle : {}),
          }}
          children={children}
        />

        {isTablet && !isCollapsed ? (
          <TabletSideNavDrawer
            isOpen={!isCollapsed}
            logoSrc={logoSrc}
            logoStyle={logoStyle}
            onClose={() => setCollapsed(true)}
          />
        ) : (
          <LayoutSideNav
            logoStyle={logoStyle}
            isCollapsed={isCollapsed}
            logoSrc={logoSrc}
            sidebarStyle={sidebarStyle}
          />
        )}
        <CompleteProfileModal
          open={showProfileModal}
          onSkip={handleSkipProfileModal}
          onConfigure={handleConfigureProfile}
        />
      </div>
    </UserPrivilegesProvider>
  );
};

export default LayOut;
